/**
 * Taskydo session on the Letswebinar origin.
 *
 * The shared login stores the JWT pair under the same keys the other
 * products use. This module turns that pair into the session object the
 * webinar screens already read.
 */
import { configureWebCore } from '@taskydo/web-core/runtime.js';

export const STORAGE_KEYS = {
  ACCESS: 'taskydo.access',
  REFRESH: 'taskydo.refresh',
  USER: 'taskydo.user',
};

export const APP = {
  name: 'Letswebinar',
  productKey: 'letswebinar',
};

const taskydoDomain = (import.meta.env.VITE_BASE_DOMAIN || 'tasky.do').toLowerCase().trim();
const local = taskydoDomain === 'localhost' || taskydoDomain.endsWith('.localhost');

export const HOST = {
  BASE_DOMAIN: (
    import.meta.env.VITE_LETSWEBINAR_BASE_DOMAIN
    || (local ? 'letswebinar.localhost' : 'letswebinar.com')
  ).toLowerCase().trim(),
  TASKYDO_DOMAIN: taskydoDomain,
  CUREDIA_DOMAIN: (
    import.meta.env.VITE_CUREDIA_BASE_DOMAIN
    || (local ? 'curedia.localhost' : 'curedia.com')
  ).toLowerCase().trim(),
};

configureWebCore({
  APP,
  STORAGE_KEYS,
  HOST,
});

export function readTokens() {
  try {
    return {
      access: localStorage.getItem(STORAGE_KEYS.ACCESS) || '',
      refresh: localStorage.getItem(STORAGE_KEYS.REFRESH) || '',
    };
  } catch {
    return { access: '', refresh: '' };
  }
}

export function writeTokens(access, refresh) {
  if (access) localStorage.setItem(STORAGE_KEYS.ACCESS, access);
  if (refresh) localStorage.setItem(STORAGE_KEYS.REFRESH, refresh);
}

export function clearSession() {
  localStorage.removeItem(STORAGE_KEYS.ACCESS);
  localStorage.removeItem(STORAGE_KEYS.REFRESH);
  localStorage.removeItem(STORAGE_KEYS.USER);
  localStorage.removeItem('user');
  localStorage.removeItem('webinarId');
}

export function webinarRole(me) {
  const level = me && me.module_access && me.module_access.webinar;
  if (me && (me.is_admin || me.is_super_admin || me.is_superuser)) return 'admin_user';
  if (level === 'admin') return 'admin_user';
  return 'view_user';
}

export function sessionFromMe(me, tokens) {
  const name = [me.first_name, me.last_name].filter(Boolean).join(' ');
  return {
    ...me,
    access: tokens.access,
    refresh: tokens.refresh,
    user_role: webinarRole(me),
    first_name: me.first_name || name || me.email || '',
    products: me.products,
  };
}

export async function loadMe(access) {
  const response = await fetch('/api/super_admin/me/', {
    headers: { Authorization: `Bearer ${access}`, Accept: 'application/json' },
  });
  if (!response.ok) {
    const error = new Error('Session expired.');
    error.status = response.status;
    throw error;
  }
  return response.json();
}

export async function signOut(refresh) {
  const token = refresh || readTokens().refresh;
  const access = readTokens().access;
  clearSession();
  const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
  if (access) headers.Authorization = `Bearer ${access}`;
  await Promise.all([
    fetch('/api/super_admin/logout/', { method: 'POST', headers }).catch(() => null),
    token
      ? fetch('/api/super_admin/token/blacklist/', {
        method: 'POST',
        headers,
        body: JSON.stringify({ refresh: token }),
      }).catch(() => null)
      : Promise.resolve(),
  ]);
}

export function isApexHost() {
  const host = window.location.hostname.toLowerCase();
  const apex = HOST.BASE_DOMAIN;
  return host === apex || host === `www.${apex}` || host === 'localhost' || host === '127.0.0.1';
}

export function sanitiseOrgCode(raw) {
  return String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '')
    .replace(/^-+|-+$/g, '');
}

export function workspaceLoginUrl(slug) {
  const { protocol, port } = window.location;
  const portSuffix = port ? `:${port}` : '';
  return `${protocol}//${slug}.${HOST.BASE_DOMAIN}${portSuffix}/login`;
}
